import { useEffect, useState } from "react";
import type { Workspace } from "../api/contract";
import { describeAction, type Tone } from "../lib/actionCopy";
import { changeMarkers, changeSections, type ChangeRow } from "../lib/changes";
import type { SectionSummary } from "./PreviewFrame";
import type { ServingController } from "../hooks/useServingChange";
import { HistoryView, type HistoryEntry, type RestorePart } from "./HistoryView";

interface ChangesViewProps {
  workspace: Workspace;
  status: { label: string; tone: Tone };
  /** Unpublished changes, newest first. */
  rows: ChangeRow[];
  /** The sections on the page the preview shows, for "where" and markers. */
  sections: SectionSummary[];
  currentFile: string | undefined;
  pageTitle: string;
  /** The row a preview marker pointed at. */
  focusedId: string | null;
  /** Submits `change.undo` for this action; resolves false when it wasn't applied. */
  onUndoChange: (actionId: string) => Promise<boolean>;
  /** Brings back the live version; resolves false when it wasn't applied. */
  onDiscard: () => Promise<boolean>;
  onRevealSection: (nodeId: string) => void;
  previewing: string | null;
  onPreview: (entry: HistoryEntry) => void;
  onDraft: () => void;
  /** Puts an earlier version live. */
  serving: ServingController;
  /** Brings back the design or brand from before a change; resolves whether it applied. */
  onRestorePart: (entry: HistoryEntry, part: RestorePart) => Promise<boolean>;
}

/** Saved changes and earlier versions. The web address and domain have their own place in the menu. */
export function ChangesView({ workspace, status, rows, sections, currentFile, pageTitle, focusedId, onUndoChange, onDiscard, onRevealSection, previewing, onPreview, onDraft, serving, onRestorePart }: ChangesViewProps) {
  const live = workspace.site.activeReleaseId !== null;
  const [busy, setBusy] = useState<string | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const markers = new Map(changeMarkers(rows, sections, currentFile).map((marker) => [marker.actionId, marker]));
  const pending = rows.filter((row) => !row.undone).length;
  const canDiscard = workspace.liveDraftId != null && pending > 0;

  useEffect(() => {
    if (focusedId) document.getElementById(`bw-change-${focusedId}`)?.scrollIntoView({ block: "nearest" });
  }, [focusedId]);

  const run = async (id: string, work: () => Promise<boolean>) => {
    setBusy(id); setFailed(null);
    const ok = await work();
    setBusy(null);
    if (!ok) setFailed(id);
  };

  return (
    <div className="bw-changes">
      <p className="bw-changes-status" role="status"><span className="bw-dot" data-tone={status.tone} aria-hidden="true" />{status.label}</p>
      {rows.length > 0 && (
        <section className="bw-changes-list" aria-labelledby="bw-unpublished-changes">
          <h3 id="bw-unpublished-changes">Not live yet</h3>
          <ol className="bw-change-list">
            {rows.map((row) => {
              const marker = markers.get(row.action.id);
              const where = changeSections(row, sections, currentFile)[0];
              const copy = describeAction(row.action);
              const what = row.action.subject?.label ? `${copy.title}: ${row.action.subject.label}` : copy.title;
              const id = row.action.id;
              return (
                <li key={id} id={`bw-change-${id}`} className="bw-change-row" data-focused={focusedId === id || undefined} data-undone={row.undone || undefined}>
                  <button type="button" className="bw-change-main" onClick={() => where && onRevealSection(where.nodeId)} disabled={!where} aria-label={`${marker ? `Change ${marker.number}. ` : ""}${what}${where ? `, ${pageTitle}, ${where.label}` : ""}`}>
                    <span className="bw-change-number" data-placed={marker ? true : undefined} aria-hidden="true">{row.number}</span>
                    <span className="bw-change-text">
                      {where && <small>{pageTitle} · {where.label}</small>}
                      <strong>{what}</strong>
                      <small>{row.undone ? "Undone" : row.action.by === "admin" ? "Stax" : "You"}</small>
                    </span>
                  </button>
                  {row.target && (
                    <button type="button" className="bw-btn bw-btn--text" disabled={busy !== null} onClick={() => void run(id, () => onUndoChange(row.target!))}>
                      {busy === id ? (row.undone ? "Redoing…" : "Undoing…") : row.undone ? "Redo" : "Undo"}
                    </button>
                  )}
                  {failed === id && <p className="bw-change-error" role="alert">{row.undone ? "Couldn’t redo this. A later change may have edited the same thing." : "Couldn’t undo this. If a later change edited the same thing, undo that first."}</p>}
                </li>
              );
            })}
          </ol>
          {canDiscard && (confirmDiscard ? (
            <div className="bw-discard-confirm" role="group" aria-label="Discard changes">
              <span>Discard {pending} unpublished {pending === 1 ? "change" : "changes"}?</span>
              <button type="button" className="bw-btn bw-btn--primary" disabled={busy !== null} onClick={() => void run("discard", onDiscard).then(() => setConfirmDiscard(false))}>{busy === "discard" ? "Discarding…" : "Discard"}</button>
              <button type="button" className="bw-btn bw-btn--quiet" disabled={busy !== null} onClick={() => setConfirmDiscard(false)}>Cancel</button>
            </div>
          ) : (
            <button type="button" className="bw-btn bw-btn--text bw-discard" onClick={() => setConfirmDiscard(true)}>Discard all</button>
          ))}
          {failed === "discard" && <p className="bw-change-error" role="alert">The changes weren’t discarded. Try again.</p>}
        </section>
      )}
      <section className="bw-changes-history" aria-labelledby="bw-earlier-versions">
        <h3 id="bw-earlier-versions">Earlier versions</h3>
        <HistoryView workspace={workspace} previewing={previewing} onPreview={onPreview} compact serving={serving} onRestorePart={onRestorePart} />
      </section>
      <nav className="bw-draft-live" aria-label="Website versions">
        <button type="button" onClick={onDraft} aria-current={previewing === null ? "page" : undefined}>Draft</button>
        {live && <a href={workspace.manifest.canonicalOrigin} target="_blank" rel="noopener noreferrer">Live site<span className="bw-sr"> (opens in a new tab)</span></a>}
      </nav>
    </div>
  );
}
